import { useCallback, useEffect, useRef, useState } from 'react';
import { FILE_LIMITS } from '@twinden/shared';

// How often the input level is sampled for the waveform.
const SAMPLE_MS = 80;
// Bars shown live while recording (the most recent levels).
const LIVE_BARS = 40;

export interface VoiceRecording {
  blob: Blob;
  fileName: string;
  /** Seconds, to one decimal. */
  duration: number;
  /** FILE_LIMITS.VOICE_WAVEFORM_BARS bar heights, 0-100. */
  waveform: number[];
}

// Squash the per-sample levels into a fixed number of bars, scaled so the
// loudest bar is full height. Quiet speech still reads as a waveform.
export function toWaveform(levels: number[], bars: number): number[] {
  if (levels.length === 0) return Array(bars).fill(0);
  const buckets: number[] = [];
  for (let i = 0; i < bars; i += 1) {
    const from = Math.floor((i * levels.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / bars));
    const slice = levels.slice(from, to);
    buckets.push(slice.reduce((sum, value) => sum + value, 0) / slice.length);
  }
  const peak = Math.max(...buckets, 0.0001);
  // Square root: speech has a wide dynamic range, and linear bars leave
  // everything but the loudest syllable looking flat.
  return buckets.map((value) => Math.round(Math.sqrt(value / peak) * 100));
}

function pickMimeType(): string | undefined {
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) =>
    MediaRecorder.isTypeSupported(type)
  );
}

function extensionFor(mime: string): string {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}

// Voice-note recording (issue #1): live level meter, a waveform captured for
// the message, cancel without sending, and an automatic stop at the limit.
export function useVoiceRecorder(
  /** Receives a recording that stopped on its own at the length limit. */
  onAutoFinish: (recording: VoiceRecording) => void
) {
  const onAutoFinishRef = useRef(onAutoFinish);
  onAutoFinishRef.current = onAutoFinish;
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [liveLevels, setLiveLevels] = useState<number[]>([]);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const levelsRef = useRef<number[]>([]);
  const startedAtRef = useRef(0);
  const samplerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Resolves the pending finish()/cancel() once the recorder has stopped.
  const settleRef = useRef<((result: VoiceRecording | null) => void) | null>(
    null
  );
  const discardRef = useRef(false);

  const teardown = useCallback(() => {
    if (samplerRef.current) clearInterval(samplerRef.current);
    samplerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
    recorderRef.current = null;
    setRecording(false);
    setLiveLevels([]);
  }, []);

  const stop = useCallback(
    (discard: boolean): Promise<VoiceRecording | null> => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        teardown();
        return Promise.resolve(null);
      }
      discardRef.current = discard;
      return new Promise((resolve) => {
        settleRef.current = resolve;
        recorder.stop();
      });
    },
    [teardown]
  );

  const finish = useCallback(() => stop(false), [stop]);
  const cancel = useCallback(() => {
    void stop(true);
  }, [stop]);

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error('Voice recording is not supported here');
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );
    recorderRef.current = recorder;
    discardRef.current = false;
    chunksRef.current = [];
    levelsRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const duration =
        Math.round((performance.now() - startedAtRef.current) / 100) / 10;
      const actualMime = recorder.mimeType.split(';')[0] || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: actualMime });
      const levels = levelsRef.current;
      teardown();

      const settle = settleRef.current;
      settleRef.current = null;
      if (discardRef.current || blob.size === 0 || duration < 1) {
        settle?.(null);
        return;
      }
      const result: VoiceRecording = {
        blob,
        fileName: `voice.${extensionFor(actualMime)}`,
        duration: Math.min(duration, FILE_LIMITS.MAX_VOICE_DURATION),
        waveform: toWaveform(levels, FILE_LIMITS.VOICE_WAVEFORM_BARS),
      };
      // No pending finish(): the length limit stopped it.
      if (settle) settle(result);
      else onAutoFinishRef.current(result);
    };

    // Level meter: RMS of the time-domain signal, sampled on a timer.
    try {
      const AudioCtor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (AudioCtor) {
        const ctx = new AudioCtor();
        audioContextRef.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buffer = new Float32Array(analyser.fftSize);

        samplerRef.current = setInterval(() => {
          analyser.getFloatTimeDomainData(buffer);
          let sum = 0;
          for (const value of buffer) sum += value * value;
          const rms = Math.sqrt(sum / buffer.length);
          levelsRef.current.push(rms);
          setLiveLevels(levelsRef.current.slice(-LIVE_BARS));
        }, SAMPLE_MS);
      }
    } catch {
      // No level meter; the recording itself still works.
    }

    startedAtRef.current = performance.now();
    setSeconds(0);
    recorder.start();
    setRecording(true);
  }, [teardown]);

  // Elapsed time, and the hard stop at the server's limit.
  useEffect(() => {
    if (!recording) return;
    const timer = setInterval(() => {
      const elapsed = (performance.now() - startedAtRef.current) / 1000;
      setSeconds(Math.floor(elapsed));
      if (elapsed >= FILE_LIMITS.MAX_VOICE_DURATION) {
        recorderRef.current?.stop();
      }
    }, 250);
    return () => clearInterval(timer);
  }, [recording]);

  // Never leave the microphone on if the composer goes away mid-recording.
  useEffect(
    () => () => {
      discardRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      else teardown();
    },
    [teardown]
  );

  return { recording, seconds, liveLevels, start, finish, cancel };
}
